import type { ClienteResumo } from '@/features/clientes/api/clientesApi';
import { IconUserEdit } from '@/shared/ui/Icon';

/** Cliente escolhido num formulário: já cadastrado ou só o nome digitado. */
export type ClienteParaCompletar =
  | { tipo: 'existente'; cliente: ClienteResumo }
  | { tipo: 'novo'; nome: string };

/**
 * Contatos que faltam no cadastro — telefone (WhatsApp do pós-venda),
 * documento e e-mail. Aniversário fica de fora: é opcional de verdade, e
 * cobrar por ele deixaria o aviso aceso para quase todo cliente.
 */
export function camposFaltandoCliente(c: ClienteResumo): string[] {
  const faltando: string[] = [];
  if (!c.telefone) faltando.push('telefone');
  if (!c.documento) faltando.push('CPF/CNPJ');
  if (!c.email) faltando.push('e-mail');
  return faltando;
}

/** "a", "a e b", "a, b e c". */
function juntarComE(itens: string[]): string {
  if (itens.length <= 1) return itens.join('');
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

/** Cliente novo (só nome) ou cadastrado faltando contato. */
export function cadastroClienteIncompleto(alvo: ClienteParaCompletar | null): boolean {
  if (!alvo) return false;
  return alvo.tipo === 'novo' || camposFaltandoCliente(alvo.cliente).length > 0;
}

function dicaDoBotao(alvo: ClienteParaCompletar | null): string {
  if (!alvo) return 'Escolha ou digite o cliente para completar o cadastro';
  if (alvo.tipo === 'novo') return `Cadastrar ${alvo.nome} agora, com telefone, CPF/CNPJ e e-mail`;
  const faltando = camposFaltandoCliente(alvo.cliente);
  if (faltando.length === 0) return 'Cadastro completo — ver ou editar';
  return `Completar cadastro — ${faltando.length === 1 ? 'falta' : 'faltam'} ${juntarComE(faltando)}`;
}

type Props = {
  /** null = nenhum cliente escolhido ainda (botão desabilitado). */
  alvo: ClienteParaCompletar | null;
  aoAbrir: () => void;
};

/**
 * Botão de ícone colado ao campo Cliente: abre o cadastro para completar
 * telefone, documento e e-mail sem sair da tela. Com o cadastro incompleto
 * ganha um ponto de alerta, e o tooltip diz exatamente o que falta.
 */
export function BotaoCompletarCliente({ alvo, aoAbrir }: Props) {
  const dica = dicaDoBotao(alvo);
  const incompleto = cadastroClienteIncompleto(alvo);
  return (
    // O span segura o tooltip: botão desabilitado não dispara eventos de mouse.
    <span className="campo-acao__envoltorio" data-tooltip-id="icon-tip" data-tooltip-content={dica}>
      <button
        type="button"
        className={`btn campo-acao${incompleto ? ' campo-acao--pendente' : ''}`}
        aria-label={dica}
        disabled={!alvo}
        onClick={aoAbrir}
      >
        <IconUserEdit aria-hidden="true" />
        {incompleto && <span className="campo-acao__alerta" aria-hidden="true" />}
      </button>
    </span>
  );
}
